import { Placement } from "@floating-ui/react";
import {
  Combobox,
  Popover,
  PopoverButtonProps,
  Portal,
} from "@headlessui/react";
import {
  CheckIcon,
  PlusIcon,
  SearchIcon,
  XIcon,
} from "@heroicons/react/outline";
import { withErrorBoundary } from "@sentry/react";
import { noop } from "lodash";
import {
  ChangeEvent,
  ReactElement,
  Ref,
  cloneElement,
  forwardRef,
  isValidElement,
  useMemo,
  useState,
} from "react";
import { usePopper } from "react-popper";

import Button, {
  ButtonInSelectProps,
  ButtonProps,
  ButtonSize,
} from "@components/button/button";
import ErrorFallback from "@components/error-fallback/error-fallback";
import Loading from "@components/loading/loading";
import { classNames } from "@helpers/css";

export const ComboboxGenericClearButton = ({
  onClearValue,
}: {
  onClearValue: () => void;
}) => (
  <div
    role="button"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClearValue();
    }}
    className="p-0.5 rounded hover:bg-gray-300/50 text-gray-400 -mx-0.5"
    aria-label="Combobox clear button"
  >
    <XIcon className="h-4 w-4 shrink-0" />
  </div>
);

const defaultButtonProps = {
  size: ButtonSize.large,
  showDownChevronIcon: true,
};

export type ComboboxGenericOption<T = unknown> = {
  label: string;
  htmlLabel?: JSX.Element;
  value: T;
  description?: string;
};

export type ComboboxGenericButtonProps = Omit<ButtonProps, "to" | "url"> & {
  ref: Ref<HTMLButtonElement>;
};

const ComboboxGenericButton = forwardRef<
  HTMLButtonElement,
  ComboboxGenericButtonProps
>((props, ref) => {
  return <Popover.Button as={Button} {...props} ref={ref} />;
});
ComboboxGenericButton.displayName = "ComboboxGenericButton";

type Props<T> = Omit<PopoverButtonProps<"div">, "children"> & {
  children?:
    | ReactElement<ComboboxGenericButtonProps>
    | React.FC<{
        value: ComboboxGenericOption<T> | null;
        clearable?: boolean;
        onClickButton?: () => void;
        setReferenceElement: (element: HTMLButtonElement) => void;
      }>;
  disabled?: boolean;
  value: ComboboxGenericOption<T> | null;
  options: ComboboxGenericOption<T>[];
  loading?: boolean;
  clearable?: boolean;
  createable?: boolean;
  createLabel?: string;
  className?: string;
  containerClassName?: string;
  placement?: Placement;
  placeholder?: string;
  searchPlaceholder?: string;
  query?: string;
  truncateOptionDescription?: boolean;
  buttonProps?: ButtonInSelectProps;
  prefix?: string;
  setQuery?: (value: string) => void;
  onClearValue?: () => void;
  onChangeValue: (option: ComboboxGenericOption<T>) => void;
  onClickCreateNew?: () => void;
  onAddNewValue?: (optionLabel: string) => void;
  onClickButton?: () => void;
};

const InternalComboboxGeneric = <T,>({
  disabled = false,
  value,
  options = [],
  loading,
  clearable = false,
  createable,
  createLabel,
  className = "",
  containerClassName = "",
  truncateOptionDescription = false,
  onClearValue,
  onChangeValue,
  onClickButton,
  onAddNewValue,
  onClickCreateNew,
  placeholder = "Not set",
  placement = "bottom-start",
  searchPlaceholder = "Search...",
  children,
  query,
  setQuery,
  prefix,
  buttonProps = defaultButtonProps,
  ...props
}: Props<T>) => {
  const [internalQuery, setInternalQuery] = useState("");
  const searchInputValue = query !== undefined ? query : internalQuery;
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
  });

  const filteredOptions = useMemo(() => {
    return options.filter(({ label }) =>
      label.toLowerCase().includes(internalQuery.toLowerCase().trim())
    );
  }, [options, internalQuery]);

  const handleChangeSearchInputValue = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setInternalQuery(event.target.value);
    if (setQuery) {
      setQuery(event.target.value);
    }
  };

  const handleChange =
    (close: () => void) => (option: ComboboxGenericOption<T> | string) => {
      close();
      if (option === searchInputValue && onAddNewValue) {
        onAddNewValue(searchInputValue);
        setInternalQuery("");
      } else {
        onChangeValue(option as ComboboxGenericOption<T>);
      }
    };

  const inputWidth = referenceElement?.getBoundingClientRect().width || 0;
  // popover width is a minium of 150px
  const popoverMinWidth = Math.max(inputWidth, 150);
  // popover width is a maximum of 640px or the width of the input
  const popoverMaxWidth = Math.max(popoverMinWidth, 640);

  if (children !== undefined && typeof children !== "function") {
    if (!isValidElement(children)) {
      throw new Error("Children must be a valid element");
    }
    if (children.type !== ComboboxGenericButton) {
      throw new Error("Children must be a ComboboxGenericButton");
    }
  }

  const showDownChevronIcon = (!clearable || !value) && !disabled;
  const mergedButtonProps = {
    ...defaultButtonProps,
    ...buttonProps,
  };
  return (
    <Popover
      className={classNames("relative", className, containerClassName)}
      {...props}
    >
      {children &&
      isValidElement(children) &&
      children.type === ComboboxGenericButton ? (
        cloneElement(children, {
          ref: setReferenceElement,
          onClick: onClickButton,
        })
      ) : children && typeof children === "function" ? (
        children({
          value,
          setReferenceElement,
          clearable,
          onClickButton,
        })
      ) : (
        <Popover.Button
          as={Button}
          className={classNames("flex-1 text-left w-full", className)}
          onClick={onClickButton}
          {...mergedButtonProps}
          showDownChevronIcon={showDownChevronIcon}
          ref={setReferenceElement}
          disabled={disabled}
        >
          {prefix && (
            <span className="font-normal text-gray-500">{prefix}:</span>
          )}
          {value?.htmlLabel ? (
            <span className="flex-1 truncate">{value.htmlLabel}</span>
          ) : value?.label ? (
            <span className="flex-1 truncate">{value.label}</span>
          ) : (
            <span className="flex-1 truncate text-gray-400 font-normal">
              {placeholder}
            </span>
          )}
          {!showDownChevronIcon &&
            clearable &&
            !!onClearValue &&
            !!value &&
            !disabled && (
              <ComboboxGenericClearButton onClearValue={onClearValue} />
            )}
        </Popover.Button>
      )}

      <Portal>
        <Popover.Panel
          className={classNames(
            "z-dropdown bg-white border rounded-md shadow-md"
          )}
          aria-label="Combobox popover"
          ref={setPopperElement}
          style={{
            ...styles.popper,
            minWidth: `${popoverMinWidth}px`,
            maxWidth: `${popoverMaxWidth}px`,
          }}
          {...attributes.popper}
        >
          {({ close }) => (
            <Combobox onChange={handleChange(close)} disabled={disabled}>
              <div className="relative">
                {createLabel && (
                  <div
                    className="text-sm text-blue-600 relative py-2 pl-4 pr-9 border-b cursor-pointer"
                    onClick={onClickCreateNew ?? noop}
                  >
                    <div className="flex items-center gap-2">
                      <PlusIcon className="h-4 w-4" />
                      <div>{createLabel}</div>
                    </div>
                  </div>
                )}
                <div className="relative w-full cursor-default">
                  <Combobox.Input
                    autoFocus
                    value={searchInputValue}
                    onChange={handleChangeSearchInputValue}
                    placeholder={searchPlaceholder}
                    aria-label="Combobox search input"
                    className="relative w-full pl-11 pr-9 py-2 text-left cursor-default sm:text-sm border-b focus:outline-0 focus:ring-0 bg-transparent"
                  />
                  <span className="absolute inset-y-0 left-0 -top-1 flex items-center pl-4">
                    <SearchIcon className="h-5 w-5 text-gray-400" />
                  </span>
                  {loading && (
                    <Loading
                      mini
                      size="4"
                      className="absolute inset-y-0 right-2 top-2.5 flex items-center"
                    />
                  )}
                </div>
                {(filteredOptions.length > 0 || createable) && (
                  <Combobox.Options
                    static
                    className="text-sm overflow-auto focus:outline-none max-h-72 divide-y divide-gray-75"
                    style={{
                      minWidth: `${popoverMinWidth}px`,
                      maxWidth: `${popoverMaxWidth}px`,
                    }}
                  >
                    {filteredOptions.map((option, i) => (
                      <Combobox.Option
                        key={`${option.value}`}
                        value={option}
                        className={({ active }) =>
                          classNames(
                            active ? "text-white bg-blue-600" : "text-gray-900",
                            "w-auto cursor-default select-none relative py-2 pl-4 pr-3",
                            filteredOptions.length - 1 === i && "rounded-b-md",
                            value?.value === option.value && "pr-8"
                          )
                        }
                      >
                        {({ active }) => (
                          <div>
                            <div className="flex justify-between gap-2 pr-5 min-w-0">
                              <div className="flex flex-1 gap-2">
                                {option.htmlLabel
                                  ? option.htmlLabel
                                  : option.label}
                              </div>
                              {value?.value === option.value && (
                                <span
                                  className={classNames(
                                    active ? "text-white" : "text-indigo-600",
                                    "absolute inset-y-0 right-0 flex items-center pr-2"
                                  )}
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </span>
                              )}
                            </div>
                            {option.description && (
                              <div
                                className={classNames(
                                  "text-2xs tracking-tight",
                                  active ? "text-white/70" : "text-gray-500",
                                  truncateOptionDescription && "truncate"
                                )}
                              >
                                {option.description}
                              </div>
                            )}
                          </div>
                        )}
                      </Combobox.Option>
                    ))}

                    {createable && searchInputValue !== "" && (
                      <Combobox.Option
                        value={searchInputValue}
                        className={({ active }) =>
                          classNames(
                            active ? "text-white bg-blue-600" : "text-gray-900",
                            "cursor-default select-none relative py-2 pl-4 pr-9"
                          )
                        }
                      >
                        <div>
                          <div className="flex justify-between gap-2 pr-5 min-w-0">
                            <div className="flex flex-1 gap-2">
                              Add new '{searchInputValue}'
                            </div>
                          </div>
                        </div>
                      </Combobox.Option>
                    )}
                  </Combobox.Options>
                )}
                {!loading &&
                  internalQuery !== "" &&
                  filteredOptions.length === 0 &&
                  !createable && (
                    <div className="py-14 px-4 text-center sm:px-14">
                      <p className="mt-4 text-sm text-gray-900">No results.</p>
                    </div>
                  )}
              </div>
            </Combobox>
          )}
        </Popover.Panel>
      </Portal>
    </Popover>
  );
};

InternalComboboxGeneric.Button = ComboboxGenericButton;

const ComboboxGeneric = withErrorBoundary(InternalComboboxGeneric, {
  fallback: ErrorFallback,
});

(ComboboxGeneric as any).Button = ComboboxGenericButton;
ComboboxGeneric.displayName = "ComboboxGeneric";

export default ComboboxGeneric as typeof InternalComboboxGeneric;
